import { DataSourceTableSelectItem } from 'src/constants/dataSourceTable';
import { DataSourceType } from 'src/constants/dataSourceTypeDescription';
import { ProjectPropertyType } from 'src/constants/project';
import { ModelRule } from 'src/containers/ProjectDiagram/types/data';
import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import {
  ConnectionInfo,
  CsvMappingInfo,
  ModelInfo,
  SensitivePropertyGroupInfo,
  VirtualGraphMappingInfo,
} from 'src/containers/PublishProjectDrawer/types';
import {
  SqlQuotingConvention,
  VirtualGraphOption,
} from 'src/store/state/dataSources';

export type ProjectFileUploadStatistics = {
  distinct: number;
  records: number;
};

export type ProjectFileUploadField = {
  value: string;
  label: string;
  statistics: ProjectFileUploadStatistics;
};

export type ProjectVirtualGraphField = {
  value: string;
  label: string;
  statistics?: never;
};

export type ProjectResourceField =
  | ProjectFileUploadField
  | ProjectVirtualGraphField;

export type ProjectFileUploadOptions = {
  escape: string;
  header: boolean;
  quote: string;
  separator: string;
  skipEmpty: boolean;
};

export const defaultProjectFileUploadOptions: ProjectFileUploadOptions = {
  escape: '"',
  header: true,
  quote: '"',
  separator: ',',
  skipEmpty: true,
};

export type ProjectFileUpload = {
  id: string;
  path: string;
  name: string;
  contents: string;
  fields: ProjectFileUploadField[];
  options: ProjectFileUploadOptions;
  dateLastUpdated: number;
  /**
   * Is set to true when the uploaded csv file is >10MiB.
   *
   * When true, this means our `contents` only contains the first 25 rows
   * of the uploaded file. (to act as a sample)
   *
   * The user will be prompted during Publish to re-upload the big csv file.
   */
  partial: boolean;
};

export type ProjectVirtualGraphOptions = Record<
  VirtualGraphOption | string,
  any
> & {
  [VirtualGraphOption.SQL_QUOTING_CONVENTION]: SqlQuotingConvention;
  [VirtualGraphOption.SQL_FUNCTIONS]: string;
  [VirtualGraphOption.PERCENT_ENCODE]: boolean;
};

export const defaultProjectVirtualGraphOptions: ProjectVirtualGraphOptions = {
  [VirtualGraphOption.SQL_QUOTING_CONVENTION]: SqlQuotingConvention.NATIVE,
  [VirtualGraphOption.SQL_FUNCTIONS]: '',
  [VirtualGraphOption.PERCENT_ENCODE]: true,
};

export type ProjectVirtualGraph = {
  id: string;
  name: string;
  connectionId: string | null;
  /** not unique! simply the name of the data source */
  dataSourceId: string;
  dataSourceType: DataSourceType | null;
  /** `string` means this is an sql query */
  from: DataSourceTableSelectItem | string;
  fields: ProjectVirtualGraphField[];
  options: ProjectVirtualGraphOptions;
  previewRows: string[][];
  dateLastUpdated: number;
  allResultsIncluded?: boolean;
};

export type ProjectResource = ProjectFileUpload | ProjectVirtualGraph;

export type ProjectModelClass = {
  iri: string;
  parents: string[];
};

export type ProjectModelProperty = {
  iri: string;
  domain: string[];
  range: string[];
  parents: string[];
  isDatatype?: boolean;
  propertyType: ProjectPropertyType;
};

export type ProjectModel = {
  connectionId: string | null;
  databaseId: string;
  databaseNamespaces: Record<string, string>;
  id: string;
  name: string;
  dateLastUpdated: number;
  classes: Record<string, ProjectModelClass> | null;
  properties: Record<string, ProjectModelProperty> | null;
  colorIdsByIri: Record<string, string> | null;
  descriptionsByIri: Record<string, string> | null;
  rules: Record<string, ModelRule> | null;
  labelsByIri: Record<string, string> | null;
  voicebox?: boolean;
  draft?: boolean;
};

export type PublishHistory = {
  connectionId: string;
  connectionInfo: ConnectionInfo;
  csvResourceHistoriesById: Record<
    string,
    Pick<CsvMappingInfo, 'creationType' | 'namedGraphIri'>
  >;
  doExportProject: boolean;
  isPublishingRules: boolean;
  isPublishingSPG: boolean;
  modelHistory: Pick<ModelInfo, 'modelName' | 'namedGraphIri'>;
  sensitivePropertyGroupsByName: Record<
    string,
    Pick<SensitivePropertyGroupInfo, 'actionType' | 'publishName'>
  >;
  vgResourceHistoriesById: Record<
    string,
    Pick<VirtualGraphMappingInfo, 'virtualGraphName'>
  >;
};

export const createEmptyPublishHistory = (): PublishHistory => ({
  connectionId: '',
  connectionInfo: {
    databaseId: '',
    namespaceIri: '',
    namespacePrefix: '',
    shouldAddNamespace: false,
  },
  csvResourceHistoriesById: {},
  doExportProject: true,
  isPublishingRules: true,
  isPublishingSPG: true,
  modelHistory: {
    modelName: '',
    namedGraphIri: '',
  },
  sensitivePropertyGroupsByName: {},
  vgResourceHistoriesById: {},
});

export type Project = {
  id: string;
  name: string;
  dateCreated: number;
  dateLastUpdated: number;
  isRemoved: boolean;
  diagram: DiagramSnapshot;
  fileUploadsById: Record<string, ProjectFileUpload>;
  virtualGraphsById: Record<string, ProjectVirtualGraph>;
  modelsById: Record<string, ProjectModel>;
  modelNamespace: string;
  dataNamespace: string;
  propertyType: ProjectPropertyType;
  publishHistory: PublishHistory;
};

export class ProjectsState {
  constructor(
    public projectIds: string[] = [],
    public projectsById: Record<string, Project> = {}
  ) {}
}
